<template>
  <div>
    <a v-if="showInviteLink" class="invite" @click="$emit('invite-referent')">{{
      $t("projetPage.infoAdministratives.participants.inviter")
    }}</a>
    <br />
    <!-- At least one referent must be associated to the projet -->
    <div v-if="!hasEnoughValidatedReferents">
      <!-- Hidden input to trigger a validation issue in correct section -->
      <div v-show="false">
        <InputWithValidation vid="participants_referents" rules="required" />
      </div>
      <b-message type="is-danger">
        {{ $t("projetPage.infoAdministratives.participants.at-least-one-referent") }}
      </b-message>
      <br />
    </div>
    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="false"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="false"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="referents"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="(column, i) in columns"
        :key="i"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div v-if="column.field === 'nouveau'">
            <b-icon
              v-if="isNewReferent(props.row.id)"
              icon="circle"
              size="is-small"
              type="is-primary"
            ></b-icon>
          </div>

          <div v-if="column.field === 'structure' && props.row.structure">
            {{ props.row.structure.nom }}
          </div>

          <div v-if="column.field === 'structure' && !props.row.structure" />

          <div v-if="column.field === 'prenom'">
            <div
              v-if="canSeeCourriel(props.row) && participating(props.row)"
              class="clickable"
              @click="showUserDetailsPage($event, props.row['id'])"
            >
              {{ props.row.prenom }}
            </div>
            <div v-else>
              {{
                $t("projetPage.infoAdministratives.participants.referentDe", [
                  props.row.structure ? props.row.structure.nom : $t("inconnu"),
                ])
              }}
            </div>
          </div>

          <div v-if="column.field === 'nom'">
            <div
              v-if="canSeeCourriel(props.row) && participating(props.row)"
              class="clickable"
              @click="showUserDetailsPage($event, props.row['id'])"
            >
              {{ props.row.nom }}
            </div>
            <div v-else>
              {{
                $t("projetPage.infoAdministratives.participants.referentDe", [
                  props.row.structure ? props.row.structure.nom : $t("inconnu"),
                ])
              }}
            </div>
          </div>

          <div v-if="column.field === 'courriel'">
            <div
              v-if="participating(props.row)"
              class="clickable"
              @click="showUserDetailsPage($event, props.row['id'])"
            >
              <!-- only clickable if participating -->
              {{ treatCourriel(props.row, false) }}
            </div>
            <div v-else>
              {{ treatCourriel(props.row, true) }}
            </div>
          </div>

          <div v-if="column.field === 'participation'">
            <div v-if="participating(props.row)" class="participating">
              {{ $t("projetPage.infoAdministratives.participants.participe") }}
              <b-icon icon="check-circle" size="is-small" class="bullet-marker"></b-icon>
            </div>
            <div v-else-if="invited(props.row)" class="invited">
              {{ $t("projetPage.infoAdministratives.participants.invitationEnvoyee") }}
            </div>
          </div>

          <div v-if="column.field === 'action'">
            <b-button
              v-if="showInviteButton(props.row)"
              :disabled="readonly"
              type="is-primary is-outlined"
              @click="invite(props.row)"
            >
              {{ $t("projetPage.infoAdministratives.participants.inviterCourt") }}
            </b-button>
            <b-button
              v-if="showQuitButton(props.row)"
              :disabled="readonly"
              type="is-danger is-outlined"
              @click="quit(props.row)"
            >
              {{ $t("projetPage.infoAdministratives.participants.quitter") }}
            </b-button>
            <b-button
              v-if="showRemove(props.row)"
              :disabled="readonly"
              type="is-warning is-outlined"
              @click="annuler(props.row)"
            >
              {{ $t("projetPage.infoAdministratives.participants.annuler") }}
            </b-button>
          </div>

          <div
            v-if="!column.custom"
            class="clickable"
            @click="showUserDetailsPage($event, props.row['id'])"
          >
            <div>{{ props.row[column.field] }}</div>
          </div>
        </template>
      </b-table-column>
    </b-table>

    <br />
    <b-table
      :ref="tableRef"
      :data="structures"
      :striped="config.isStriped"
      :hoverable="false"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      :loading="isLoading"
      :class="{
        clickable: !isPorteurProjet,
      }"
      @click="handleStructureClick"
    >
      <b-table-column v-slot="props" field="name" label="Nom de la structure" width="120" sortable>
        {{ props.row.nom }}
      </b-table-column>
      <b-table-column v-slot="props" field="type" label="Type" width="80" sortable>
        {{ props.row.type }}
      </b-table-column>
      <b-table-column v-slot="props" field="name" width="120" label="Adresse" sortable>
        {{ props.row.rue }}<br />
        {{ props.row.codePostal }} {{ props.row.ville }}
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { eventBus } from "@/services/EventBus"
import Column from "@/views/tables/Column"
import {
  FilterDTO,
  PaginationOrder,
  ProjetAnnuelDTO,
  StructureDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { SortOrder } from "@/model/SortOrder"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { LoginService } from "@/services/rest/login/LoginService"
import { UsersService } from "@/services/rest/users/UsersService"
// @ts-ignore: Buefy 3.0 type definitions have temporary issues
import { BButton, BIcon, BMessage, BTable, BTableColumn } from "buefy"
import ObfuscationUtils from "@/services/ObfuscationUtils"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { useTableView } from "@/hooks/useTableView"

export default defineComponent({
  name: "ProjetReferentsView",
  components: {
    InputWithValidation,
    BButton,
    BMessage,
    BIcon,
    BTable,
    BTableColumn,
  },
  props: {
    projetAnnuel: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: false,
      default: null,
    },
    invitations: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    showInviteLink: {
      type: Boolean,
      required: false,
    },
    isPorteurProjet: {
      type: Boolean,
      required: false,
    },
  },

  emits: [
    "invite-referent",
    "invite-to-join-referent",
    "open-profile-modale",
    "dirtify-project",
    "projet-annuel-updated",
    "invitations-updated",
    "trigger-validation",
  ],
  data() {
    return {
      loginService: LoginService.INSTANCE,
      usersService: UsersService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      loggedUser: new UtilisateurDTO(),
      referents: [] as Array<UtilisateurDTO>,
      hasEnoughValidatedReferents: false,
      structures: [] as Array<StructureDTO>,
      tableView: null as any,
    }
  },
  computed: {
    tableRef(): string {
      return this.tableView?.state.tableRef || ""
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter || {}
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config || {}
    },
    activeFilters(): Array<any> {
      return this.tableView?.state.activeFilters || []
    },
  },
  watch: {
    "projetAnnuel.referents"(): void {
      this.reloadData()
    },
  },
  created(): void {
    this.tableView = useTableView({
      tableRef: "projet-referents-table",
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
    })

    const loggedUser = this.loginService.getLoggedUser()
    if (!loggedUser || !loggedUser.id) {
      return
    }
    this.loggedUser = loggedUser
  },
  mounted(): void {
    this.tableView?.initMounted()
  },
  methods: {
    // Méthodes du hook useTableView
    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },
    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },
    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },

    // Méthodes métier
    async reloadData(): Promise<void> {
      if (!this.projetAnnuel) {
        this.referents = []
        this.updateTotal(0)
        this.hasEnoughValidatedReferents = false
        this.structures = []
        return
      }
      this.referents = JSON.parse(JSON.stringify(this.projetAnnuel.referents))
      this.updateTotal(this.referents.length)
      this.hasEnoughValidatedReferents =
        this.projetAnnuel.referents.length - this.nbReferentEnAttente() > 0
      this.sortReferents()
      this.$emit("trigger-validation")
    },
    getInitialColumns(): Array<Column> {
      return [
        {
          field: "nouveau",
          label: this.$t("nouveau").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "structure",
          label: this.$t("registerPage.structure").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "prenom",
          label: this.$t("graePage.membres.prenom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "nom",
          label: this.$t("graePage.membres.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "courriel",
          label: this.$t("graePage.membres.courriel").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "participation",
          label: "",
          sortable: true,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "action",
          label: this.$t("projetPage.actions").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
      ]
    },
    getDefaultSort(): Array<PaginationOrder> {
      const nameSort = new PaginationOrder()
      nameSort.clause = "nom"
      nameSort.desc = true
      return [nameSort]
    },
    showUserDetailsPage(event: Event, id: number): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      //For APP to open ProfileModale
      eventBus.emit("open-profile-modale", { show: true, id: id.toString() })
    },
    dirtifyProject(withRedirect: boolean): void {
      this.$emit("dirtify-project", withRedirect)
    },

    invite(referent: UtilisateurDTO): void {
      this.$emit("invite-to-join-referent", referent)
      this.dirtifyProject(false)
    },

    quit(referent: UtilisateurDTO): void {
      if (this.projectHasMoreThanOneReferent()) {
        this.$buefy.dialog.confirm({
          message: this.$t("projet.infos_administratives.areYouSureToRemoveUser").toString(),
          confirmText: this.$t("confirmer").toString(),
          cancelText: this.$t("annuler").toString(),
          onConfirm: () => this.removeUser(referent),
        })
      } else {
        this.infoReportingService.dialog(
          this.$t("projet.infos_administratives.at-least-one-title", ["référent"]).toString(),
          this.$t("projet.infos_administratives.at-least-one-message", ["ce référent"]).toString(),
          "OK",
          () => {
            // Nothing to do
          },
          "is-warning",
        )
      }
    },
    annuler(referent: UtilisateurDTO): void {
      if (this.nbReferentEnAttente() > 0) {
        this.$buefy.dialog.confirm({
          message: this.$t(
            "projet.infos_administratives.areYouSureToRemoveInvitationUser2",
          ).toString(),
          confirmText: this.$t("confirmer").toString(),
          cancelText: this.$t("annuler").toString(),
          onConfirm: () => this.removeInvitation(referent),
        })
      } else {
        this.infoReportingService.dialog(
          this.$t("projet.infos_administratives.at-least-one-title", ["référent"]).toString(),
          this.$t("projet.infos_administratives.at-least-one-message", ["ce référent"]).toString(),
          "OK",
          () => {
            // Nothing to do
          },
          "is-warning",
        )
      }
    },
    removeInvitation(referent: UtilisateurDTO) {
      // Copy + Emit pattern pour éviter les mutations directes de props
      const updatedProjetAnnuel = {
        ...this.projetAnnuel,
        consentementEnAttente: this.projetAnnuel.consentementEnAttente.filter(
          (element) => element !== referent.id,
        ),
      }
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      this.removeUser(referent)
    },
    removeUser(referent: UtilisateurDTO) {
      // Copy + Emit pattern pour éviter les mutations directes de props
      const updatedProjetAnnuel = {
        ...this.projetAnnuel,
        referents: this.projetAnnuel.referents.filter((element) => element.id !== referent.id),
      }
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      this.dirtifyProject(this.loggedUser.id === referent.id)
    },
    showInviteButton(toDelete: UtilisateurDTO): boolean {
      return (
        (this.isPorteurProjet ||
          this.usersService.isAccompagnateurRegional(this.loggedUser) ||
          this.usersService.isAccompagnateurNationalOrAdmin(this.loggedUser)) &&
        !this.isLinked(toDelete)
      )
    },
    showQuitButton(toDelete: UtilisateurDTO): boolean {
      return this.canDeleteReferents(toDelete) && !this.invitations.includes(toDelete.courriel)
    },
    canDeleteReferents(toDelete: UtilisateurDTO): boolean {
      return (
        (this.isPorteurProjet ||
          this.usersService.isAccompagnateurRegional(this.loggedUser) ||
          this.usersService.isAccompagnateurNationalOrAdmin(this.loggedUser)) &&
        this.isLinked(toDelete) &&
        !this.hasConsentementEnAttente(toDelete)
      )
    },
    isLinked(toDelete: UtilisateurDTO): boolean {
      return this.projetAnnuel.referents.some((utilisateur) => utilisateur.id === toDelete.id)
    },
    hasConsentementEnAttente(toDelete: UtilisateurDTO): boolean {
      return this.projetAnnuel.consentementEnAttente.some((id) => id === toDelete.id)
    },
    projectHasMoreThanOneReferent(): boolean {
      return this.projetAnnuel.referents.length - this.nbReferentEnAttente() > 1
    },
    nbReferentEnAttente(): number {
      return (
        this.projetAnnuel.consentementEnAttente.filter((id) =>
          this.projetAnnuel.referents.some((r) => r.id === id),
        ).length + this.invitations.length
      )
    },
    participating(user: UtilisateurDTO): boolean {
      if (user.id) {
        return (
          this.projetAnnuel.referents.some((referent) => referent.id === user.id) &&
          this.projetAnnuel.consentementEnAttente.indexOf(user.id) === -1 &&
          !this.invitations.includes(user.courriel)
        )
      } else {
        return false
      }
    },
    showRemove(user: UtilisateurDTO): boolean {
      const role = this.loggedUser.profil
      const roles = [
        PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
        PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
        PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
      ]
      if (user.id && roles.includes(role)) {
        return this.projetAnnuel.consentementEnAttente.indexOf(user.id) !== -1
      } else {
        return false
      }
    },
    invited(user: UtilisateurDTO): boolean {
      if (user.id) {
        return this.projetAnnuel.consentementEnAttente.indexOf(user.id) !== -1
      } else {
        return false
      }
    },
    isNewReferent(referentId: string): boolean {
      return this.projetAnnuel.nouveauxActeurs.some((acteur) => referentId === acteur.id)
    },
    treatCourriel(user: UtilisateurDTO, forceObfuscation: boolean): string {
      if (this.canSeeCourriel(user) && !forceObfuscation) {
        return user.courriel
      }
      return ObfuscationUtils.obfuscateCourriel(user.courriel)
    },
    canSeeCourriel(user: UtilisateurDTO): boolean {
      return (
        user.acceptNameVisibilityOnProtectedListing ||
        user.id === this.loggedUser.id ||
        ObfuscationUtils.hasRightToSeeCourriel(user, this.loggedUser)
      )
    },
    sortReferents(): void {
      // Sort
      const participatingReferents = this.referents.filter((ref) => this.participating(ref))
      const structureIds: string[] = []
      this.structures = []
      participatingReferents.forEach((ref) => {
        if (ref.structure && ref.structure.id && structureIds.indexOf(ref.structure.id) < 0) {
          structureIds.push(ref.structure.id)
          this.structures.push(ref.structure)
        }
      })
      const participatingReferentsIds = participatingReferents
        .filter((ref) => ref.id !== null && ref.id !== undefined)
        .map((ref) => ref.id)
      this.referents = this.referents.sort((ref1, ref2) => {
        // Me first
        if (ref1.id == this.loggedUser.id) {
          return -1
        }
        if (ref2.id == this.loggedUser.id) {
          return 1
        }

        // Then participating
        const ref1Participating = participatingReferentsIds.indexOf(ref1.id) > -1
        const ref2Participating = participatingReferentsIds.indexOf(ref2.id) > -1
        if (ref1Participating != ref2Participating) {
          return ref1Participating ? -1 : 1
        }

        // Sort per structure
        if (ref1.structure && ref2.structure && ref1.structure.nom != ref2.structure.nom) {
          return ref1.structure.nom.localeCompare(ref2.structure.nom)
        }

        // Sort alphabetically then
        return ref1.courriel.localeCompare(ref2.courriel)
      })
    },
    showStructureDetailsPage(s: StructureDTO): void {
      this.$router.push("/structure/" + s.id + "/details")
    },
    handleStructureClick(event: Event): void {
      if (!this.isPorteurProjet) {
        // @ts-ignore - event.target peut être undefined mais sera géré par le routeur
        const row = event.currentTarget?.closest("tr")
        if (row) {
          const rowIndex = Array.from(row.parentElement?.children || []).indexOf(row)
          const structure = this.structures[rowIndex]
          if (structure) {
            this.showStructureDetailsPage(structure)
          }
        }
      }
    },
  },
})
</script>
²
<style scoped lang="scss">
@import "@/styles/_variables";

.participating {
  text-align: center;
  color: $green-v2;
}

.invited {
  text-align: center;
  color: $green-v2;
}

.clickable {
  cursor: pointer;
}

.invite {
  font-family: $font-family-title;
}
</style>
